/**
 * SITE EVENTS — first-class "what's on" content (HTBH today).
 *
 * Deliberately SEPARATE from enrolment `programs`: a program is something a
 * family pays for and registers a child into; an event is anything the church
 * wants people to know about — a weekly playgroup, a fortnightly pantry, a
 * one-off carols night. An event may POINT at a program (register_program_id)
 * or at an external URL for registration, but it never carries money.
 *
 * Times are Australia/Sydney WALL-CLOCK strings, "YYYY-MM-DDTHH:MM", never
 * UTC instants: "Tuesdays 9:30am" stays 9:30am across daylight saving, and an
 * operator types what the noticeboard says. The helpers below convert to an
 * offset only where a machine needs one (JSON-LD, .ics).
 *
 * Everything here is pure (no fetch, no DOM) so the Worker, the site and the
 * prerender/sitemap scripts all agree on what "upcoming" and "past" mean.
 */
import { z } from "zod";

export const SITE_TIMEZONE = "Australia/Sydney";

export const SiteEventStatus = z.enum(["draft", "published"]);
export type SiteEventStatus = z.infer<typeof SiteEventStatus>;

export const SiteEventRecurrence = z.enum(["none", "weekly", "fortnightly", "monthly"]);
export type SiteEventRecurrence = z.infer<typeof SiteEventRecurrence>;

/** "YYYY-MM-DDTHH:MM" Sydney wall-clock. */
export const LOCAL_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
export const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const localDateTime = z.string().regex(LOCAL_DATETIME_RE, "use YYYY-MM-DDTHH:MM (Sydney time)");
const optionalText = (max: number) => z.string().max(max).default("");

/** One event row as the API returns it. */
export const SiteEvent = z.object({
  id: z.number().int(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  /** Markdown-lite body (paragraphs, ## headings, - lists, **bold**, *em*,
   *  [links](https://…)). Rendered by the site WITHOUT innerHTML. */
  body: z.string(),
  start_at: z.string(),
  end_at: z.string().nullable(),
  all_day: z.number().int(),
  recurrence: SiteEventRecurrence,
  /** Last date (inclusive) a recurring event runs, YYYY-MM-DD. Null = open-ended. */
  recurrence_until: z.string().nullable(),
  /** Shown next to the rule, e.g. "School terms only". */
  recurrence_note: z.string(),
  /** Free-text "when" that REPLACES the automatic description, for schedules
   *  a rule can't express ("Selected Fridays — dates to be confirmed"). */
  when_text: z.string(),
  location_name: z.string(),
  location_address: z.string(),
  image_url: z.string(),
  image_alt: z.string(),
  /** CSS object-position for the hero crop, e.g. "50% 30%". */
  image_focus: z.string(),
  ministry: z.string(),
  featured: z.number().int(),
  status: SiteEventStatus,
  seo_title: z.string(),
  seo_description: z.string(),
  og_image: z.string(),
  register_program_id: z.number().int().nullable(),
  register_url: z.string(),
  register_label: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  updated_by: z.string().nullable(),
});
export type SiteEvent = z.infer<typeof SiteEvent>;

/** Admin create/update body. `id` present = update. `slug` blank = derive from title. */
export const SiteEventInput = z
  .object({
    id: z.number().int().optional(),
    title: z.string().trim().min(1, "title is required").max(160),
    slug: z.string().trim().max(80).default(""),
    summary: optionalText(400),
    body: optionalText(20_000),
    start_at: localDateTime,
    end_at: localDateTime.nullable().or(z.literal("").transform(() => null)).default(null),
    all_day: z.union([z.boolean(), z.number()]).transform((v) => (v ? 1 : 0)).default(0),
    recurrence: SiteEventRecurrence.default("none"),
    recurrence_until: z
      .string()
      .regex(LOCAL_DATE_RE, "use YYYY-MM-DD")
      .nullable()
      .or(z.literal("").transform(() => null))
      .default(null),
    recurrence_note: optionalText(200),
    when_text: optionalText(200),
    location_name: optionalText(200),
    location_address: optionalText(300),
    image_url: optionalText(1000),
    image_alt: optionalText(300),
    image_focus: z
      .string()
      .regex(/^(\d{1,3}% \d{1,3}%)?$/, 'use "X% Y%"')
      .default(""),
    ministry: optionalText(80),
    featured: z.union([z.boolean(), z.number()]).transform((v) => (v ? 1 : 0)).default(0),
    status: SiteEventStatus.default("draft"),
    seo_title: optionalText(120),
    seo_description: optionalText(300),
    og_image: optionalText(1000),
    register_program_id: z.number().int().positive().nullable().default(null),
    register_url: z
      .string()
      .max(1000)
      .refine((v) => v === "" || /^(https?:\/\/|\/)/.test(v), "use a full https:// URL or a /path on this site")
      .default(""),
    register_label: optionalText(60),
  })
  .refine((e) => e.end_at === null || e.end_at >= e.start_at, {
    message: "the event can't end before it starts",
    path: ["end_at"],
  });
export type SiteEventInput = z.input<typeof SiteEventInput>;
export type SiteEventInputParsed = z.output<typeof SiteEventInput>;

/* ───────────────────────────── slugs ─────────────────────────────── */

export function slugify(title: string): string {
  return title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/@/g, " at ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 70)
    .replace(/-+$/g, "") || "event";
}

/* ─────────────────────────── Sydney clock ─────────────────────────── */

const pad = (n: number) => String(n).padStart(2, "0");

/** The current Sydney wall-clock as "YYYY-MM-DDTHH:MM". */
export function sydneyNowLocal(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SITE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

/** UTC offset ("+11:00") Sydney observes at a given wall-clock time. */
export function sydneyOffset(local: string): string {
  const asUtc = new Date(`${local.slice(0, 16)}:00Z`);
  if (Number.isNaN(asUtc.getTime())) return "+10:00";
  const name =
    new Intl.DateTimeFormat("en-US", { timeZone: SITE_TIMEZONE, timeZoneName: "longOffset" })
      .formatToParts(asUtc)
      .find((p) => p.type === "timeZoneName")?.value ?? "GMT+10:00";
  const m = /GMT([+-]\d{2}):?(\d{2})?/.exec(name);
  return m ? `${m[1]}:${m[2] ?? "00"}` : "+10:00";
}

/** "2026-10-27T09:30" → "2026-10-27T09:30:00+11:00". */
export function toOffsetIso(local: string): string {
  return `${local.slice(0, 16)}:00${sydneyOffset(local)}`;
}

/* ───────────────────────── date arithmetic ────────────────────────── */

function dateOf(local: string): Date {
  return new Date(`${local.slice(0, 10)}T00:00:00Z`);
}
function ymd(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
export function addDaysLocal(local: string, days: number): string {
  const d = dateOf(local);
  d.setUTCDate(d.getUTCDate() + days);
  return ymd(d) + local.slice(10);
}
function minutesBetween(a: string, b: string): number {
  return (new Date(`${b}:00Z`).getTime() - new Date(`${a}:00Z`).getTime()) / 60_000;
}
function addMinutesLocal(local: string, minutes: number): string {
  const d = new Date(`${local}:00Z`);
  d.setUTCMinutes(d.getUTCMinutes() + minutes);
  return `${ymd(d)}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ORDINAL = ["first", "second", "third", "fourth", "last"];

/** 1-based week-of-month of a date (5 = the fifth, treated as "last"). */
function nthOfMonth(local: string): number {
  return Math.ceil(dateOf(local).getUTCDate() / 7);
}

/** The nth (1-4, or 5 = last) given weekday of a year/month, as YYYY-MM-DD. */
function nthWeekdayOf(year: number, month: number, weekday: number, nth: number): string {
  if (nth >= 5) {
    const last = new Date(Date.UTC(year, month + 1, 0));
    const back = (last.getUTCDay() - weekday + 7) % 7;
    last.setUTCDate(last.getUTCDate() - back);
    return ymd(last);
  }
  const first = new Date(Date.UTC(year, month, 1));
  const fwd = (weekday - first.getUTCDay() + 7) % 7;
  first.setUTCDate(1 + fwd + (nth - 1) * 7);
  return ymd(first);
}

/** Where one occurrence ends: its end time, or the end of its day. */
function occurrenceEnd(start: string, durationMin: number, allDay: boolean): string {
  if (allDay || durationMin <= 0) {
    const endDay = durationMin > 0 ? addMinutesLocal(start, durationMin) : start;
    return `${endDay.slice(0, 10)}T23:59`;
  }
  return addMinutesLocal(start, durationMin);
}

type Timing = Pick<SiteEvent, "start_at" | "end_at" | "all_day" | "recurrence" | "recurrence_until">;

/**
 * The start of the next occurrence that has NOT finished by `nowLocal`
 * (an event in progress counts), or null when the event is over for good.
 */
export function nextOccurrence(e: Timing, nowLocal: string = sydneyNowLocal()): string | null {
  const allDay = e.all_day === 1;
  const duration = e.end_at ? Math.max(0, minutesBetween(e.start_at, e.end_at)) : 0;
  const until = e.recurrence_until ? `${e.recurrence_until}T23:59` : null;

  if (e.recurrence === "none") {
    return occurrenceEnd(e.start_at, duration, allDay) >= nowLocal ? e.start_at : null;
  }

  // A recurring event's end_at only carries the TIME of each occurrence; its
  // date is the anchor's (a multi-day recurring occurrence is not a thing).
  const dur = e.end_at
    ? Math.max(0, minutesBetween(e.start_at, `${e.start_at.slice(0, 10)}${e.end_at.slice(10)}`))
    : 0;

  let occ = e.start_at;
  if (e.recurrence === "weekly" || e.recurrence === "fortnightly") {
    const step = e.recurrence === "weekly" ? 7 : 14;
    // Jump close to now in one go rather than iterating from an old anchor.
    const gapDays = Math.floor(minutesBetween(e.start_at, nowLocal) / (24 * 60));
    if (gapDays > step) occ = addDaysLocal(occ, Math.floor((gapDays - step) / step) * step);
    for (let i = 0; i < 1000 && occurrenceEnd(occ, dur, allDay) < nowLocal; i++) {
      occ = addDaysLocal(occ, step);
    }
  } else {
    const anchor = dateOf(e.start_at);
    const weekday = anchor.getUTCDay();
    const nth = nthOfMonth(e.start_at);
    let y = anchor.getUTCFullYear();
    let m = anchor.getUTCMonth();
    const time = e.start_at.slice(10);
    for (let i = 0; i < 1200 && occurrenceEnd(occ, dur, allDay) < nowLocal; i++) {
      m += 1;
      if (m > 11) {
        m = 0;
        y += 1;
      }
      occ = nthWeekdayOf(y, m, weekday, nth) + time;
    }
  }
  if (until && occ > until) return null;
  return occ;
}

export function isEventPast(e: Timing, nowLocal: string = sydneyNowLocal()): boolean {
  return nextOccurrence(e, nowLocal) === null;
}

/* ─────────────────────────── wording ─────────────────────────────── */

/** "09:30" → "9:30am", "13:00" → "1pm", "12:00" → "12pm". */
export function formatClock(hhmm: string): string {
  const [hS, mS] = hhmm.split(":");
  const h = Number(hS);
  const m = Number(mS);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;
  const suffix = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${pad(m)}${suffix}`;
}

/** "9:30–11:45am", "1:30–2:30pm", "11am–1pm"; "" for all-day. */
export function formatTimeRange(e: Pick<SiteEvent, "start_at" | "end_at" | "all_day">): string {
  if (e.all_day === 1) return "";
  const start = e.start_at.slice(11, 16);
  const end = e.end_at ? e.end_at.slice(11, 16) : null;
  if (!end || end === start) return formatClock(start);
  const s = formatClock(start);
  const t = formatClock(end);
  const sameHalf = (Number(start.slice(0, 2)) >= 12) === (Number(end.slice(0, 2)) >= 12);
  return sameHalf ? `${s.replace(/(am|pm)$/, "")}–${t}` : `${s}–${t}`;
}

/** "Tue 27 Oct 2026" (year omitted when `withYear` is false). */
export function formatLocalDate(local: string, withYear = true): string {
  const d = dateOf(local);
  if (Number.isNaN(d.getTime())) return local;
  const base = `${DOW_SHORT[d.getUTCDay()]} ${d.getUTCDate()} ${MON_SHORT[d.getUTCMonth()]}`;
  return withYear ? `${base} ${d.getUTCFullYear()}` : base;
}

/** "Every Tuesday", "Every second Friday", "Monthly, first Saturday". */
export function describeRecurrence(e: Pick<SiteEvent, "start_at" | "recurrence">): string {
  const dow = DOW[dateOf(e.start_at).getUTCDay()] ?? "";
  switch (e.recurrence) {
    case "weekly":
      return `Every ${dow}`;
    case "fortnightly":
      return `Every second ${dow}`;
    case "monthly":
      return `Monthly, ${ORDINAL[nthOfMonth(e.start_at) - 1] ?? "last"} ${dow}`;
    default:
      return "";
  }
}

/**
 * The one-line "when" a card shows: the host's own wording if they gave one,
 * else the rule ("Every Tuesday · 9:30–11:45am") or the dates of a one-off
 * ("Wed 30 Sep – Fri 2 Oct 2026 · 9am–3pm").
 */
export function describeWhen(
  e: Pick<SiteEvent, "start_at" | "end_at" | "all_day" | "recurrence" | "when_text">,
): string {
  if (e.when_text.trim()) return e.when_text.trim();
  const time = formatTimeRange(e);
  if (e.recurrence !== "none") {
    return [describeRecurrence(e), time].filter(Boolean).join(" · ");
  }
  const startDay = e.start_at.slice(0, 10);
  const endDay = e.end_at ? e.end_at.slice(0, 10) : startDay;
  if (endDay !== startDay) {
    const sameYear = startDay.slice(0, 4) === endDay.slice(0, 4);
    const range = `${formatLocalDate(startDay, !sameYear)} – ${formatLocalDate(endDay)}`;
    return [range, e.all_day === 1 ? "" : time].filter(Boolean).join(" · ");
  }
  return [formatLocalDate(startDay), time].filter(Boolean).join(" · ");
}

/* ───────────────────────── list ordering ─────────────────────────── */

/**
 * Upcoming published events in display order: FEATURED first, then by next
 * occurrence — except that events with custom "when" wording (whose computed
 * dates are not trusted) follow the dated ones. Past events are dropped.
 * Pure — the Worker and the site share it.
 */
export function upcomingEvents<
  T extends Timing & Pick<SiteEvent, "featured" | "status"> & { when_text?: string },
>(events: T[], nowLocal: string = sydneyNowLocal()): Array<T & { next_at: string }> {
  const vague = (e: T) => (e.when_text?.trim() ? 1 : 0);
  return events
    .filter((e) => e.status === "published")
    .map((e) => ({ ...e, next_at: nextOccurrence(e, nowLocal) }))
    .filter((e): e is T & { next_at: string } => e.next_at !== null)
    .sort((a, b) => b.featured - a.featured || vague(a) - vague(b) || a.next_at.localeCompare(b.next_at));
}

/* ───────────────────────── calendar file ─────────────────────────── */

function icsEscape(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/([,;])/g, "\\$1");
}
function icsLocal(local: string): string {
  return local.replace(/[-:]/g, "").slice(0, 13) + "00";
}

/** An RFC 5545 calendar with one VEVENT (and its RRULE when recurring). */
export function eventIcs(e: SiteEvent, siteUrl: string, nowLocal: string = sydneyNowLocal()): string {
  const url = `${siteUrl.replace(/\/$/, "")}/events/${e.slug}`;
  const allDay = e.all_day === 1;
  const start = e.recurrence === "none" ? e.start_at : (nextOccurrence(e, nowLocal) ?? e.start_at);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Alight Creative//Site events//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:event-${e.id}@${new URL(url).host}`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`,
    `SUMMARY:${icsEscape(e.title)}`,
  ];
  if (allDay) {
    const endDay = e.end_at && e.recurrence === "none" ? e.end_at.slice(0, 10) : start.slice(0, 10);
    lines.push(`DTSTART;VALUE=DATE:${start.slice(0, 10).replace(/-/g, "")}`);
    lines.push(`DTEND;VALUE=DATE:${addDaysLocal(endDay, 1).slice(0, 10).replace(/-/g, "")}`);
  } else {
    lines.push(`DTSTART;TZID=${SITE_TIMEZONE}:${icsLocal(start)}`);
    const end =
      e.end_at && e.recurrence === "none"
        ? e.end_at
        : e.end_at
          ? `${start.slice(0, 10)}${e.end_at.slice(10)}`
          : addMinutesLocal(start, 60);
    lines.push(`DTEND;TZID=${SITE_TIMEZONE}:${icsLocal(end)}`);
  }
  if (e.recurrence !== "none") {
    const until = e.recurrence_until ? `;UNTIL=${e.recurrence_until.replace(/-/g, "")}T235959Z` : "";
    const rule =
      e.recurrence === "weekly"
        ? "FREQ=WEEKLY"
        : e.recurrence === "fortnightly"
          ? "FREQ=WEEKLY;INTERVAL=2"
          : (() => {
              const nth = nthOfMonth(e.start_at);
              const day = DOW_SHORT[dateOf(e.start_at).getUTCDay()]!.slice(0, 2).toUpperCase();
              return `FREQ=MONTHLY;BYDAY=${nth >= 5 ? -1 : nth}${day}`;
            })();
    lines.push(`RRULE:${rule}${until}`);
  }
  const where = [e.location_name, e.location_address].filter(Boolean).join(", ");
  if (where) lines.push(`LOCATION:${icsEscape(where)}`);
  const desc = [e.summary, e.recurrence_note, url].filter(Boolean).join("\n\n");
  lines.push(`DESCRIPTION:${icsEscape(desc)}`);
  lines.push(`URL:${url}`);
  lines.push("END:VEVENT", "END:VCALENDAR");
  // RFC 5545: CRLF line endings, lines folded at 75 octets.
  return lines
    .map((l) => (l.length <= 74 ? l : l.match(/.{1,73}/g)!.join("\r\n ")))
    .join("\r\n");
}

/** "https://maps.google.com/?q=…" for the event's place, or "" when it has none. */
export function mapLink(e: Pick<SiteEvent, "location_name" | "location_address">): string {
  const q = [e.location_name, e.location_address].filter(Boolean).join(", ");
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : "";
}
